import {
  AfterViewInit,
  Component,
  inject,
  input,
  OnDestroy,
  output,
  signal
} from '@angular/core';
import {catchError, EMPTY} from 'rxjs';
import {CheckoutService} from 'src/app/course/services/checkout.service';
import {ButtonComponent} from '../buttons/button/button.component';
import {LoadingSpinnerComponent} from '../loading-spinner/loading-spinner.component';

declare let braintree;

@Component({
  selector: 'app-braintree-payment',
  standalone: true,
  imports: [ButtonComponent, LoadingSpinnerComponent],
  templateUrl: './braintree-payment.component.html',
  styleUrl: './braintree-payment.component.scss'
})
export class BraintreePaymentComponent implements OnDestroy, AfterViewInit {
  private checkoutService = inject(CheckoutService);

  courseWebAppId = input<string>();
  cartId = input<string>();

  paymentValidityChange = output<boolean>();
  loadingChange = output<boolean>();
  paymentComplete = output();
  errorCompletingPayment = output();

  paymentToken: any;
  dropinInstance: any;
  braintreeDropInLoaded = signal(false);

  ngAfterViewInit() {
    this.initializeBraintreeDropIn();
  }

  initializeBraintreeDropIn() {
    const paymentTokenData = localStorage.getItem('paymentToken');

    let paymentToken: string = null;

    if (paymentTokenData) {
      paymentToken = JSON.parse(paymentTokenData)?.token;
    }

    if (!paymentToken) {
      this.getCoursePaymentToken();
      return;
    }

    this.createBraintreeDropIn(paymentToken);
  }

  getCoursePaymentToken() {
    this.checkoutService
      .getPaymentToken(this.courseWebAppId())
      .pipe(
        catchError((error: any) => {
          console.error('Error getting payment token:', error);

          return EMPTY;
        })
      )
      .subscribe((response: any) => {
        if (!response) {
          return;
        }

        const expirationTime = new Date().getTime() + 1 * 60 * 60 * 1000; // 1 hour from now

        localStorage.setItem(
          'paymentToken',
          JSON.stringify({token: response, expirationTime: expirationTime})
        );

        this.createBraintreeDropIn(response);
      });
  }

  createBraintreeDropIn(token: string) {
    braintree.dropin.create(
      {
        authorization: token,
        selector: '#dropin-container',
        vaultManager: true,
        card: {
          overrides: {
            fields: {
              number: {placeholder: 'Card Number'},
              cvv: {placeholder: '123'}
            }
          }
        }
      },
      (createErr, instance) => {
        if (createErr) {
          console.error('Error creating drop-in:', createErr);
          return;
        }

        window.document.querySelectorAll('.braintree-form__notice-of-collection > a').forEach((element) => {
          element.innerHTML = 'By paying with my card, I agree to this transaction’s privacy policy.';
        });

        this.braintreeDropInLoaded.set(true);
        this.dropinInstance = instance;
        this.dropinInstance.on('card:validityChange', (event: any) => {
          let arefieldsValid = true;

          for (let key of Object.keys(event.fields)) {
            const field = event.fields[key];

            if (!field.isValid) {
              arefieldsValid = false;
              break;
            }
          }
          ;

          if (arefieldsValid) {
            this.paymentValidityChange.emit(true);
          } else {
            this.paymentValidityChange.emit(false);
          }
        });
        this.dropinInstance.on('cardTypeChange', function(event) {
          if (event.cards.length === 1) {
            console.log(event.cards[0].type);
          } else {
            console.log('Type of card not yet known');
          }
        });
      }
    );
    // braintree.dataCollector.create({
    //   dataCollector: true
    // });
  }

  ngOnDestroy() {
    localStorage.removeItem('paymentToken');

    if (this.dropinInstance) {
      this.dropinInstance.teardown();
    }
  }

  completePayment() {
    this.dropinInstance.requestPaymentMethod((requestPaymentMethodErr, payload) => {
      if (requestPaymentMethodErr) {
        alert(requestPaymentMethodErr.error.message);
        this.loadingChange.emit(false);
        localStorage.removeItem('paymentToken');
        return;
      }

      this.loadingChange.emit(true);

      this.checkoutService
        .placeOrder(this.cartId(), payload.nonce)
        .pipe(
          catchError((error: any) => {
            console.error('Error getting payment token:', error);
            localStorage.removeItem('paymentToken');
            this.loadingChange.emit(false);
            this.errorCompletingPayment.emit();
            return EMPTY;
          })
        )
        .subscribe((response) => {
          localStorage.removeItem('paymentToken');

          this.loadingChange.emit(false);
          this.paymentComplete.emit();
        });
    });
  }
}
